import { useEffect, useRef, useState } from "react";

// Six main Hawaiian islands. `value` matches the `island` field in properties.json;
// `slug` is the URL segment.
export const ISLANDS = [
  { value: "Oahu",       display: "Oʻahu",            slug: "oahu",       blurb: "Waikīkī · North Shore · Honolulu" },
  { value: "Maui",       display: "Maui",             slug: "maui",       blurb: "Beaches · Wailea · Road to Hāna" },
  { value: "Kauai",      display: "Kauaʻi",           slug: "kauai",      blurb: "Nā Pali · Poʻipū · Princeville" },
  { value: "Big Island", display: "Hawaiʻi Island",   slug: "big-island", blurb: "Kona · Volcanoes · Mauna Kea" },
  { value: "Molokai",    display: "Molokaʻi",         slug: "molokai",    blurb: "Empty beaches · sea cliffs" },
  { value: "Lanai",      display: "Lānaʻi",           slug: "lanai",      blurb: "Hulopoʻe Bay · luxury escapes" }
];

// Fallback centers for the /rates coord search.
export const ISLAND_CENTERS = {
  Oahu:        { lat: 21.4389, lng: -158.0001, radius: 30000 },
  Maui:        { lat: 20.7984, lng: -156.3319, radius: 40000 },
  Kauai:       { lat: 22.0964, lng: -159.5261, radius: 30000 },
  "Big Island":{ lat: 19.5429, lng: -155.6659, radius: 80000 },
  Molokai:     { lat: 21.1444, lng: -157.0225, radius: 25000 },
  Lanai:       { lat: 20.8283, lng: -156.9199, radius: 20000 }
};

// Debounced server-side suggest fetch. The full search index lives on the
// server; we hit /api/search-suggest?q=… and get back only what matches.
// Empty query returns the island shortlist locally without a network call.
export function useSuggestions(query, { enabled = true, delay = 180 } = {}) {
  const [data, setData] = useState({ islands: ISLANDS, places: [], properties: [] });
  const controllerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    const needle = (query || "").trim();
    if (!needle) { setData({ islands: ISLANDS, places: [], properties: [] }); return; }

    const timer = setTimeout(async () => {
      controllerRef.current?.abort();
      const ctl = new AbortController();
      controllerRef.current = ctl;
      try {
        const r = await fetch(`/api/search-suggest?q=${encodeURIComponent(needle)}&limit=3`, { signal: ctl.signal });
        if (!r.ok) return;
        const json = await r.json();
        // Island section filtered client-side (tiny — no need to round-trip)
        const islands = ISLANDS.filter(i =>
          i.value.toLowerCase().includes(needle.toLowerCase()) ||
          i.display.toLowerCase().includes(needle.toLowerCase()) ||
          i.blurb.toLowerCase().includes(needle.toLowerCase())
        ).slice(0, 3);
        setData({ islands, places: json.places || [], properties: json.properties || [] });
      } catch { /* abort or network — ignore */ }
    }, delay);

    return () => { clearTimeout(timer); controllerRef.current?.abort(); };
  }, [query, enabled, delay]);

  return data;
}

export function slugifyName(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

// Island name (as stored in properties.json) → URL slug
export function islandSlug(name) {
  return ({ Oahu: "oahu", Maui: "maui", Kauai: "kauai", "Big Island": "big-island", Molokai: "molokai", Lanai: "lanai" })[name] || "";
}
